import React, { useEffect, useMemo, useRef } from 'react'
import Box from '@mui/material/Box'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import './map.css'
import { createRoot } from 'react-dom/client'
import moment from 'moment'
import AmbulanceIcon from '../../../../assets/icons/Ambulance'
import { getLocationCoordinates } from '../../proximity'
import API from '../../../../services/API'

const ROUTE_COLORS = ['#239AA4', '#F59E0B', '#A855F7', '#EF4444', '#22C55E']
const STALE_LOCATION_THRESHOLD_MS = 5 * 60 * 1000

// 'fresh' = updated recently, 'stale' = hasn't reported in a while (could be
// a dead GPS feed, not just a stationary vehicle), 'unknown' = never reported.
const getLocationStaleness = (duty) => {
  const at = duty?.vehicle?.lastLocationUpdate?.at
  if (!at) return 'unknown'
  return Date.now() - new Date(at).getTime() > STALE_LOCATION_THRESHOLD_MS
    ? 'stale'
    : 'fresh'
}
const ACTIVE_JOURNEY_STATUSES = [
  'Crew Acknowledged',
  'Mobile',
  'On Scene',
  'Patient Onboard',
  'Transporting',
  'Arrived',
  'Handover Complete',
]
const createStopMarkerElement = ({
  number,
  stopType, // 'pickup' | 'dropoff'
  routeIndex = 0,
}) => {
  const routeColor = ROUTE_COLORS[routeIndex % ROUTE_COLORS.length]
  const isPickup = stopType === 'pickup'

  const wrapper = document.createElement('div')
  wrapper.style.position = 'relative'
  wrapper.style.width = '34px'
  wrapper.style.height = '42px'
  wrapper.style.display = 'flex'
  wrapper.style.alignItems = 'center'
  wrapper.style.justifyContent = 'center'
  wrapper.style.pointerEvents = 'auto'
  wrapper.style.cursor = 'pointer'

  const pin = document.createElement('div')
  pin.style.width = '28px'
  pin.style.height = '28px'
  pin.style.borderRadius = '999px'
  pin.style.background = isPickup ? '#3B82F6' : '#10B981'
  pin.style.border = `3px solid ${routeColor}`
  pin.style.boxShadow = '0 8px 18px rgba(0,0,0,0.35)'
  pin.style.display = 'flex'
  pin.style.alignItems = 'center'
  pin.style.justifyContent = 'center'
  pin.style.color = '#fff'
  pin.style.fontSize = '12px'
  pin.style.fontWeight = '800'
  pin.style.lineHeight = '1'
  pin.style.fontFamily = 'Inter, Arial, sans-serif'
  pin.textContent = String(number)

  const tail = document.createElement('div')
  tail.style.position = 'absolute'
  tail.style.bottom = '2px'
  tail.style.left = '50%'
  tail.style.transform = 'translateX(-50%)'
  tail.style.width = '0'
  tail.style.height = '0'
  tail.style.borderLeft = '6px solid transparent'
  tail.style.borderRight = '6px solid transparent'
  tail.style.borderTop = `10px solid ${isPickup ? '#3B82F6' : '#10B981'}`

  const typeBadge = document.createElement('div')
  typeBadge.style.position = 'absolute'
  typeBadge.style.top = '-4px'
  typeBadge.style.right = '-4px'
  typeBadge.style.minWidth = '16px'
  typeBadge.style.height = '16px'
  typeBadge.style.padding = '0 4px'
  typeBadge.style.borderRadius = '999px'
  typeBadge.style.background = 'rgba(15,23,42,0.95)'
  typeBadge.style.border = `1px solid ${routeColor}`
  typeBadge.style.color = '#E2E8F0'
  typeBadge.style.fontSize = '9px'
  typeBadge.style.fontWeight = '800'
  typeBadge.style.display = 'flex'
  typeBadge.style.alignItems = 'center'
  typeBadge.style.justifyContent = 'center'
  typeBadge.textContent = isPickup ? 'P' : 'D'

  const routeBadge = document.createElement('div')
  routeBadge.style.position = 'absolute'
  routeBadge.style.bottom = '-2px'
  routeBadge.style.left = '-6px'
  routeBadge.style.minWidth = '16px'
  routeBadge.style.height = '16px'
  routeBadge.style.padding = '0 4px'
  routeBadge.style.borderRadius = '999px'
  routeBadge.style.background = routeColor
  routeBadge.style.color = '#fff'
  routeBadge.style.fontSize = '9px'
  routeBadge.style.fontWeight = '800'
  routeBadge.style.display = 'flex'
  routeBadge.style.alignItems = 'center'
  routeBadge.style.justifyContent = 'center'
  routeBadge.textContent = `R${routeIndex + 1}`

  wrapper.appendChild(tail)
  wrapper.appendChild(pin)
  wrapper.appendChild(typeBadge)
  wrapper.appendChild(routeBadge)

  return wrapper
}

const normalizePostcode = (value = '') =>
  value.replace(/\s+/g, '').trim().toUpperCase()

const getRouteLineCoords = (route) => {
  const journeys = route?.journeys ?? []
  if (!journeys.length) return []

  const coords = []

  journeys.forEach((journey) => {
    const pickup = getLocationCoordinates(journey?.pickup)
    if (pickup?.length === 2) {
      coords.push(pickup)
    }
  })

  const finalDropoff = journeys.find(
    (journey) => getLocationCoordinates(journey?.dropoff)?.length === 2,
  )
  const finalDropoffCoords = getLocationCoordinates(finalDropoff?.dropoff)

  if (finalDropoffCoords) {
    coords.push(finalDropoffCoords)
  }

  return coords
}

const getDisplayRoute = ({ selectedRoute, selectedDutyInfo }) => {
  if (selectedRoute) return selectedRoute

  const routes = selectedDutyInfo?.routes ?? []
  for (const route of routes) {
    const activeJourney = route?.journeys?.find((journey) =>
      ACTIVE_JOURNEY_STATUSES.includes(journey?.status),
    )

    if (activeJourney) {
      return {
        ...route,
        journeys: [activeJourney],
      }
    }
  }

  return null
}

const fetchRouteGeometry = async (coords, signal) => {
  if (coords.length < 2) return coords

  const coordinatePath = coords
    .map(([lng, lat]) => `${Number(lng)},${Number(lat)}`)
    .join(';')

  // Routed server-side so patient/hospital coordinates never leave the
  // browser to a third party directly — see routers/patientTransport/controlroom.js
  const res = await API.get('/pts/controlroom/route-geometry', {
    params: { coords: coordinatePath },
    signal,
  })
  if (res.status !== 200) throw new Error('Unable to fetch route geometry')

  return res.data?.routes?.[0]?.geometry?.coordinates?.length
    ? res.data.routes[0].geometry.coordinates
    : coords
}

const Map = ({
  duties = [],
  selectedDuty,
  selectedDutyInfo,
  selectedRoute,
  setSelectedDuty,
  setSelectedRoute,
}) => {
  const mapContainer = useRef(null)
  const mapRef = useRef(null)
  const markersRef = useRef(new globalThis.Map())
  const routeMarkersRef = useRef([])
  const hasFitAllVehiclesRef = useRef(false)

  const routeGeometryKey = useMemo(() => {
    const routeToDisplay = getDisplayRoute({ selectedRoute, selectedDutyInfo })

    return JSON.stringify(
      {
        dutyId: selectedDutyInfo?._id ?? selectedDuty?._id ?? null,
        selectedRouteId: routeToDisplay?._id ?? null,
        route: routeToDisplay
          ? {
              id: routeToDisplay?._id,
              journeys: (routeToDisplay?.journeys ?? []).map((journey) => ({
            id: journey?._id,
            pickup: getLocationCoordinates(journey?.pickup),
            dropoff: getLocationCoordinates(journey?.dropoff),
              })),
            }
          : null,
      },
    )
  }, [selectedDuty?._id, selectedDutyInfo?._id, selectedDutyInfo?.routes, selectedRoute])

  const clearRoutes = () => {
    routeMarkersRef.current.forEach((m) => m.remove())
    routeMarkersRef.current = []

    if (mapRef.current?.getLayer('route-lines')) {
      mapRef.current.removeLayer('route-lines')
    }

    if (mapRef.current?.getSource('route-lines')) {
      mapRef.current.removeSource('route-lines')
    }
  }

  useEffect(() => {
    if (mapRef.current) return

    mapRef.current = new maplibregl.Map({
      container: mapContainer.current,
      // style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
      style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
      center: [-1.5, 52.5],
      zoom: 6,
      attributionControl: true,
    })

    mapRef.current.addControl(new maplibregl.NavigationControl(), 'bottom-left')
  }, [])

  // Draw selected duty / selected route stops + lines
  useEffect(() => {
    if (!mapRef.current) return
    const abortController = new AbortController()

    clearRoutes()

    const routeToDisplay = getDisplayRoute({ selectedRoute, selectedDutyInfo })
    const routesToDisplay = routeToDisplay ? [routeToDisplay] : []

    if (!routesToDisplay.length) return

    const bounds = new maplibregl.LngLatBounds()
    const lineFeatures = []
    let hasPoints = false

    routesToDisplay.forEach((route, routeIndex) => {
      let stopNumber = 1

      route.journeys?.forEach((journey) => {
        const pickup = getLocationCoordinates(journey?.pickup)

        if (pickup?.length === 2) {
          const marker = new maplibregl.Marker({
            element: createStopMarkerElement({
              number: stopNumber,
              stopType: 'pickup',
              routeIndex,
            }),
            anchor: 'bottom',
          })
            .setLngLat(pickup)
            .addTo(mapRef.current)

          routeMarkersRef.current.push(marker)
          bounds.extend(pickup)
          hasPoints = true
          stopNumber += 1
        }
      })

      const finalDropoff = route.journeys?.find(
        (journey) => getLocationCoordinates(journey?.dropoff)?.length === 2,
      )
      const finalDropoffCoords = getLocationCoordinates(finalDropoff?.dropoff)

      if (finalDropoffCoords) {
        const marker = new maplibregl.Marker({
          element: createStopMarkerElement({
            number: stopNumber,
            stopType: 'dropoff',
            routeIndex,
          }),
          anchor: 'bottom',
        })
          .setLngLat(finalDropoffCoords)
          .addTo(mapRef.current)

        routeMarkersRef.current.push(marker)
        bounds.extend(finalDropoffCoords)
        hasPoints = true
      }

      const lineCoords = getRouteLineCoords(route)

      if (lineCoords.length >= 2) {
        lineFeatures.push({
          type: 'Feature',
          properties: {
            routeIndex,
            routeColor: ROUTE_COLORS[routeIndex % ROUTE_COLORS.length],
          },
          geometry: {
            type: 'LineString',
            coordinates: lineCoords,
          },
        })
      }
    })

    const drawLines = (features) => {
      if (!features.length || !mapRef.current) return

      mapRef.current.addSource('route-lines', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
            features,
        },
      })

      mapRef.current.addLayer({
        id: 'route-lines',
        type: 'line',
        source: 'route-lines',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': ['get', 'routeColor'],
          'line-width': selectedRoute ? 5 : 4,
          'line-opacity': selectedRoute ? 1 : 0.85,
        },
      })
    }

    drawLines(lineFeatures)

    Promise.all(
      lineFeatures.map(async (feature) => {
        try {
          const routedCoords = await fetchRouteGeometry(
            feature.geometry.coordinates,
            abortController.signal,
          )
          return {
            ...feature,
            geometry: {
              ...feature.geometry,
              coordinates: routedCoords,
            },
          }
        } catch {
          return feature
        }
      }),
    ).then((routedFeatures) => {
      if (abortController.signal.aborted || !mapRef.current) return
      const source = mapRef.current.getSource('route-lines')
      source?.setData({
        type: 'FeatureCollection',
        features: routedFeatures,
      })
    })

    if (hasPoints && !bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, {
        padding: {
          top: 170,
          right: selectedRoute || selectedDutyInfo ? 430 : 40,
          bottom: 60,
          left: 50,
        },
        duration: 800,
        maxZoom: 14,
      })
    }

    return () => abortController.abort()
  }, [routeGeometryKey])

  // Build once, then move vehicle markers in place as locations update.
  useEffect(() => {
    if (!mapRef.current) return

    const seenDutyIds = new Set()

    duties.forEach((duty) => {
      const coords = duty?.vehicle?.location?.coordinates
      if (!coords || coords.length < 2) return

      const [lng, lat] = coords
      if (Number.isNaN(lng) || Number.isNaN(lat)) return
      seenDutyIds.add(duty._id)

      const existing = markersRef.current.get(duty._id)
      if (existing) {
        existing.marker.setLngLat([lng, lat])
        existing.duty = duty
        return
      }

      const el = document.createElement('div')
      el.dataset.dutyId = duty._id
      const root = createRoot(el)
      el._root = root

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(mapRef.current)

      markersRef.current.set(duty._id, { marker, el, duty })
    })

    markersRef.current.forEach(({ marker, el }, dutyId) => {
      if (seenDutyIds.has(dutyId)) return
      marker.remove()
      el?._root?.unmount?.()
      markersRef.current.delete(dutyId)
    })
  }, [duties])

  // Update vehicle marker styling
  useEffect(() => {
    markersRef.current.forEach(({ el, duty }) => {
      const isSelected = selectedDuty?._id === duty._id
      const isNotSelected = selectedDuty && selectedDuty?._id !== duty._id
      const staleness = getLocationStaleness(duty)
      const isStale = staleness !== 'fresh'

      el.style.width = isSelected ? '92px' : '78px'
      el.style.height = '36px'
      el.style.filter = isSelected
        ? // ? 'drop-shadow(0 0 10px rgba(96,165,250,0.7))'
          'drop-shadow(0 0 10px rgb(234, 255, 0))'
        : 'none'
      el.style.cursor = 'pointer'
      el.title = isStale
        ? staleness === 'unknown'
          ? `${duty?.vehicle?.callSign || 'Vehicle'}: no location reported yet`
          : `${duty?.vehicle?.callSign || 'Vehicle'}: location last updated ${moment(duty.vehicle.lastLocationUpdate.at).fromNow()} — may not be current`
        : ''

      el._root.render(
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.6,
            borderRadius: 1,
            position: 'relative',
            bgcolor: isSelected
              ? 'rgba(15,23,42,0.96)'
              : 'rgba(15,23,42,0.82)',
            border: isSelected
              ? '1px solid rgba(234,255,0,0.65)'
              : isStale
                ? '1px solid rgba(245,158,11,0.55)'
                : '1px solid rgba(255,255,255,0.18)',
            color: isNotSelected ? 'rgba(226,232,240,0.62)' : '#E2E8F0',
            boxShadow: '0 8px 18px rgba(0,0,0,0.35)',
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isSelected ? '#239AA4' : '#67E8F9',
              flexShrink: 0,
            }}
          >
            <AmbulanceIcon sx={{ width: '100%', height: '100%' }} />
          </Box>
          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: isSelected ? '0.82rem' : '0.74rem',
              fontWeight: 900,
              lineHeight: 1,
            }}
          >
            {duty?.vehicle?.callSign || duty?.dutyRef || 'PTS'}
          </Box>
          {isStale && (
            <Box
              sx={{
                position: 'absolute',
                top: -5,
                right: -5,
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: '#F59E0B',
                border: '2px solid #0F172A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '9px',
                fontWeight: 900,
                color: '#0F172A',
              }}
            >
              !
            </Box>
          )}
        </Box>,
      )

      el.onclick = () => {
        setSelectedRoute?.(null)
        setSelectedDuty?.(duty)
      }
    })
  }, [duties, selectedDuty, setSelectedDuty, setSelectedRoute])

  // Fit all vehicles when nothing selected
  useEffect(() => {
    if (!mapRef.current) return
    if (!duties.length) return
    if (selectedDuty) return
    if (hasFitAllVehiclesRef.current) return

    const bounds = new maplibregl.LngLatBounds()
    let hasPoints = false

    duties.forEach((duty) => {
      const coords = duty?.vehicle?.location?.coordinates
      if (!coords || coords.length < 2) return

      const [lng, lat] = coords
      if (Number.isNaN(lng) || Number.isNaN(lat)) return

      bounds.extend([lng, lat])
      hasPoints = true
    })

    if (hasPoints) {
      hasFitAllVehiclesRef.current = true
      mapRef.current.fitBounds(bounds, {
        padding: {
          top: 100,
          right: 50,
          bottom: 60,
          left: 50,
        },
        duration: 800,
        maxZoom: 13,
      })
    }
  }, [duties, selectedDuty])

  // Only fly to vehicle on selection if there are no route stops yet.
  useEffect(() => {
    if (!mapRef.current || !selectedDuty) return

    const hasRouteStops =
      !!selectedRoute ||
      !!selectedDutyInfo?.routes?.some((route) =>
        route?.journeys?.some(
          (j) =>
            getLocationCoordinates(j?.pickup)?.length === 2 ||
            getLocationCoordinates(j?.dropoff)?.length === 2,
        ),
      )

    if (hasRouteStops) return

    const coords = selectedDuty?.vehicle?.location?.coordinates
    if (!coords || coords.length < 2) return

    const [lng, lat] = coords
    if (Number.isNaN(lng) || Number.isNaN(lat)) return

    mapRef.current.flyTo({
      center: [lng, lat],
      zoom: 13,
      speed: 0.9,
      curve: 1.2,
      essential: true,
    })
  }, [selectedDuty?._id, routeGeometryKey])

  return (
    <div
      ref={mapContainer}
      style={{
        width: '100%',
        height: '100%',
      }}
    />
  )
}

export default Map
